import type {
  AccessMode,
  CallSpan,
  Message,
  Phase,
  SessionDetail,
  SessionSummary,
  Source,
  Turn,
  TurnStatus,
  TurnStep,
} from "../../../shared/api.ts";

export type UserMessage = Extract<Message, { role: "user" }>;
export type AssistantMessage = Extract<Message, { role: "assistant" }>;
export type HistoryMatchMessage = Extract<Message, { kind: "history_match" }>;
export type ReuseMessage = Extract<Message, { kind: "reuse" }>;
export type NoticeMessage = Extract<Message, { kind: "notice" }>;

export const ACTIVE_STATUSES: TurnStatus[] = ["queued", "running"];

/** The ChatGPT backend (subscription, development login) reports no cache writes, so the cost estimates them. */
export function writesUnreported(accessMode: AccessMode | null): boolean {
  return accessMode === "subscription" || accessMode === "dev";
}

export function isActiveStatus(status: TurnStatus): boolean {
  return status === "queued" || status === "running";
}

// Phases ------------------------------------------------------------------------

/**
 * The three phases of the track. code_check and fact belong to Tariff, and so do evidence and lookup, the researcher
 * looking that fact up; research_more belongs to Research.
 */
export type TrackPhase = "research" | "tariff" | "decisions";
export const TRACK_PHASES: TrackPhase[] = ["research", "tariff", "decisions"];

export function trackPhaseOf(phase: Phase | null | undefined): TrackPhase | null {
  switch (phase) {
    case "research":
    case "research_more":
      return "research";
    case "tariff":
    case "code_check":
    case "fact":
    case "evidence":
    case "lookup":
      return "tariff";
    case "enrichment":
      return "decisions";
    default:
      return null;
  }
}

function time(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const value = Date.parse(iso);
  return Number.isNaN(value) ? null : value;
}

export function sortSteps(steps: TurnStep[]): TurnStep[] {
  return [...steps].sort((a, b) => a.seq - b.seq);
}

/** A step's duration, or null for notes (`info`) and running steps. */
export function stepDurationMs(step: TurnStep): number | null {
  if (step.status === "info" || step.status === "running") return null;
  const start = time(step.startedAt);
  const end = time(step.finishedAt);
  if (start === null || end === null) return null;
  return Math.max(0, end - start);
}

/** Consecutive steps of one phase, as the process list shows them. */
export type StepGroup = {
  key: string;
  phase: Phase;
  track: TrackPhase | null;
  steps: TurnStep[];
  startMs: number;
  endMs: number;
  running: boolean;
  failed: boolean;
};

/**
 * When a call ran: until it finished, or until now while the turn runs. A call a stopped turn left open ends where it
 * started, as nothing says how long it lasted.
 */
function callBounds(call: CallSpan, running: boolean, now: number): { startMs: number; endMs: number } | null {
  const start = time(call.startedAt);
  if (start === null) return null;
  const end = time(call.finishedAt) ?? (running ? now : start);
  return { startMs: start, endMs: Math.max(start, end) };
}

/**
 * Groups steps into consecutive runs of one phase. A run lasts from its first step, or the start of a call its steps
 * came from, until its latest step or call ends, or until now while one of its steps runs. A call starts before its
 * first step and streams its answer after its last one. Pauses between runs are not counted in either run.
 */
export function groupSteps(steps: TurnStep[], options: { running: boolean; now: number; calls?: CallSpan[] }): StepGroup[] {
  const calls = new Map((options.calls ?? []).map((call) => [call.id, call]));
  const sorted = sortSteps(steps);
  const groups: StepGroup[] = [];
  for (const step of sorted) {
    const last = groups[groups.length - 1];
    const start = time(step.startedAt) ?? options.now;
    if (last && last.phase === step.phase) {
      last.steps.push(step);
    } else {
      groups.push({
        key: `${step.phase}-${step.seq}`,
        phase: step.phase,
        track: trackPhaseOf(step.phase),
        steps: [step],
        startMs: start,
        endMs: start,
        running: false,
        failed: false,
      });
    }
  }
  groups.forEach((group, index) => {
    const last = index === groups.length - 1;
    group.running = group.steps.some((step) => step.status === "running");
    group.failed = group.steps.some((step) => step.status === "failed");
    let start = group.startMs;
    let end = group.startMs;
    for (const step of group.steps) {
      end = Math.max(end, time(step.finishedAt) ?? time(step.startedAt) ?? end);
    }
    const own = new Set(group.steps.map((step) => step.callId));
    for (const id of own) {
      const call = id ? calls.get(id) : undefined;
      const bounds = call ? callBounds(call, options.running, options.now) : null;
      if (bounds) {
        start = Math.min(start, bounds.startMs);
        end = Math.max(end, bounds.endMs);
      }
    }
    // While the turn runs, the latest group lasts until now, or until a later call that is not its own started.
    let until = options.now;
    for (const call of calls.values()) {
      const callStart = time(call.startedAt);
      if (callStart !== null && callStart >= end && !own.has(call.id)) until = Math.min(until, callStart);
    }
    group.startMs = start;
    group.endMs = options.running && (group.running || last) ? Math.max(end, group.running ? options.now : until) : end;
  });
  return groups;
}

export type TrackSegment = {
  phase: TrackPhase;
  /** skipped: the turn succeeded without it. missed: the turn stopped before it. */
  state: "done" | "current" | "pending" | "skipped" | "missed";
  durationMs: number | null;
};

/**
 * The timing a turn's track is checked against, and its model calls. Each is optional, as the admin trace has only
 * the duration and the calls.
 */
export type TrackTurn = { status: TurnStatus; phase: Phase | null } & Partial<Pick<Turn, "durationMs" | "startedAt" | "finishedAt" | "calls">>;

/**
 * The Research → Tariff → Decisions track: what is done, what runs now, and how long each took.
 * A phase's time is the time its groups and its model calls cover, each moment once. The calls count because a call
 * runs before its first step and streams its answer after its last one; in turn 81c7e6dc the steps alone left out
 * 1 min 43 s of 3 min 45 s. Groups of one phase can overlap, as the level steps of a walk that ran twice did when both
 * walks shared their ids, and their sum would count that time twice. No phase takes longer than the turn.
 */
export function trackSegments(steps: TurnStep[], turn: TrackTurn, now: number): TrackSegment[] {
  const running = isActiveStatus(turn.status);
  const calls = turn.calls ?? [];
  const groups = groupSteps(steps, { running: turn.status === "running", now, calls });
  const spans = new Map<TrackPhase, Array<{ startMs: number; endMs: number }>>();
  const add = (phase: TrackPhase | null, span: { startMs: number; endMs: number } | null) => {
    if (phase && span) spans.set(phase, [...(spans.get(phase) ?? []), span]);
  };
  for (const group of groups) add(group.track, group);
  // A call that left no step still took its time.
  for (const call of calls) add(trackPhaseOf(call.phase), callBounds(call, turn.status === "running", now));
  const elapsed = turnElapsedMs(turn, now);
  const totals = new Map<TrackPhase, number>();
  for (const [phase, list] of spans) {
    const covered = coveredMs(list);
    totals.set(phase, elapsed === null ? covered : Math.min(covered, elapsed));
  }
  const lastTracked = [...groups].reverse().find((group) => group.track)?.track ?? null;
  const current = running ? (trackPhaseOf(turn.phase) ?? lastTracked) : null;
  const currentIndex = current ? TRACK_PHASES.indexOf(current) : -1;

  return TRACK_PHASES.map((phase, index) => {
    const duration = totals.get(phase) ?? null;
    if (phase === current) {
      return { phase, state: "current", durationMs: duration };
    }
    if (duration !== null) {
      return { phase, state: "done", durationMs: duration };
    }
    if (running && (currentIndex === -1 || index > currentIndex)) {
      return { phase, state: "pending", durationMs: null };
    }
    if (running) return { phase, state: "pending", durationMs: null };
    return { phase, state: turn.status === "succeeded" ? "skipped" : "missed", durationMs: null };
  });
}

/** The time spans cover together: overlapping spans count once. */
function coveredMs(spans: Array<{ startMs: number; endMs: number }>): number {
  const sorted = [...spans].sort((a, b) => a.startMs - b.startMs);
  let total = 0;
  let open: { startMs: number; endMs: number } | null = null;
  for (const span of sorted) {
    const endMs = Math.max(span.startMs, span.endMs);
    if (open && span.startMs <= open.endMs) {
      open.endMs = Math.max(open.endMs, endMs);
      continue;
    }
    if (open) total += open.endMs - open.startMs;
    open = { startMs: span.startMs, endMs };
  }
  return open ? total + open.endMs - open.startMs : total;
}

/** How long the turn has run: finished turns use `durationMs`, running ones count from `startedAt`. */
export function turnElapsedMs(turn: Pick<Turn, "status"> & Partial<Pick<Turn, "durationMs" | "startedAt" | "finishedAt">>, now: number): number | null {
  const duration = turn.durationMs ?? null;
  if (duration !== null && !isActiveStatus(turn.status)) return duration;
  const start = time(turn.startedAt);
  if (start === null) return null;
  const end = time(turn.finishedAt) ?? now;
  return Math.max(0, end - start);
}

// Cache patches for live events -----------------------------------------------

function replaceTurn(detail: SessionDetail, turnId: string, update: (turn: Turn) => Turn): SessionDetail {
  let found = false;
  const turns = detail.turns.map((turn) => {
    if (turn.id !== turnId) return turn;
    found = true;
    return update(turn);
  });
  return found ? { ...detail, turns } : detail;
}

/** A full turn from `snapshot` or `done`. */
export function upsertTurn(detail: SessionDetail, turn: Turn): SessionDetail {
  if (detail.turns.some((existing) => existing.id === turn.id)) {
    return replaceTurn(detail, turn.id, () => ({ ...turn, steps: sortSteps(turn.steps) }));
  }
  return { ...detail, turns: [...detail.turns, { ...turn, steps: sortSteps(turn.steps) }] };
}

/** A `turn` event: everything but the steps. */
export function patchTurn(detail: SessionDetail, patch: Omit<Turn, "steps">): SessionDetail {
  return replaceTurn(detail, patch.id, (turn) => ({ ...turn, ...patch, steps: turn.steps }));
}

/** A `step` event: a new step, or a newer state of one. */
export function upsertStep(detail: SessionDetail, turnId: string, step: TurnStep): SessionDetail {
  return replaceTurn(detail, turnId, (turn) => {
    const index = turn.steps.findIndex((existing) => existing.id === step.id);
    const steps = index === -1 ? [...turn.steps, step] : turn.steps.map((existing, i) => (i === index ? step : existing));
    return { ...turn, steps: sortSteps(steps) };
  });
}

/** A `draft` event. The contract sends `{ text }`; it is treated as the whole draft so far. */
export function setReplyDraft(detail: SessionDetail, turnId: string, text: string): SessionDetail {
  return replaceTurn(detail, turnId, (turn) => ({ ...turn, replyDraft: text }));
}

// Transcript --------------------------------------------------------------------

export type TranscriptItem =
  | { type: "user"; key: string; message: UserMessage }
  | { type: "turn"; key: string; turn: Turn; reply: AssistantMessage | null; isLatest: boolean }
  | { type: "assistant"; key: string; message: AssistantMessage }
  | { type: "history"; key: string; message: HistoryMatchMessage }
  | { type: "reuse"; key: string; message: ReuseMessage }
  | { type: "notice"; key: string; message: NoticeMessage };

function byTime(a: { createdAt: string }, b: { createdAt: string }): number {
  return (time(a.createdAt) ?? 0) - (time(b.createdAt) ?? 0);
}

/**
 * Messages and turns, oldest first. Each user message is followed by its history match (if any),
 * then by the turn whose `userMessageId` is that message, which carries the assistant reply.
 */
export function buildTranscript(detail: SessionDetail): TranscriptItem[] {
  const messages = [...detail.messages].sort(byTime);
  const turns = [...detail.turns].sort((a, b) => (time(a.queuedAt) ?? 0) - (time(b.queuedAt) ?? 0));
  const turnsByMessage = new Map<string, Turn[]>();
  for (const turn of turns) {
    const list = turnsByMessage.get(turn.userMessageId) ?? [];
    list.push(turn);
    turnsByMessage.set(turn.userMessageId, list);
  }
  const replies = new Map<string, AssistantMessage>();
  for (const message of messages) {
    if (message.role === "assistant" && !replies.has(message.turnId)) {
      replies.set(message.turnId, message);
    }
  }
  const matchesByMessage = new Map<string, HistoryMatchMessage[]>();
  for (const message of messages) {
    if (message.kind === "history_match") {
      const list = matchesByMessage.get(message.forMessageId) ?? [];
      list.push(message);
      matchesByMessage.set(message.forMessageId, list);
    }
  }

  const latestTurnId = turns[turns.length - 1]?.id ?? null;
  const placedMessages = new Set<string>();
  const placedTurns = new Set<string>();
  const items: TranscriptItem[] = [];

  const pushTurn = (turn: Turn) => {
    if (placedTurns.has(turn.id)) return;
    placedTurns.add(turn.id);
    const reply = replies.get(turn.id) ?? null;
    if (reply) placedMessages.add(reply.id);
    items.push({ type: "turn", key: `turn-${turn.id}`, turn, reply, isLatest: turn.id === latestTurnId });
  };

  for (const message of messages) {
    if (placedMessages.has(message.id)) continue;
    if (message.role === "user") {
      placedMessages.add(message.id);
      items.push({ type: "user", key: message.id, message });
      for (const match of matchesByMessage.get(message.id) ?? []) {
        placedMessages.add(match.id);
        items.push({ type: "history", key: match.id, message: match });
      }
      for (const turn of turnsByMessage.get(message.id) ?? []) {
        pushTurn(turn);
      }
      continue;
    }
    placedMessages.add(message.id);
    if (message.role === "assistant") {
      const turn = turns.find((candidate) => candidate.id === message.turnId);
      if (turn) {
        pushTurn(turn);
      } else {
        items.push({ type: "assistant", key: message.id, message });
      }
    } else if (message.kind === "history_match") {
      items.push({ type: "history", key: message.id, message });
    } else if (message.kind === "reuse") {
      items.push({ type: "reuse", key: message.id, message });
    } else {
      items.push({ type: "notice", key: message.id, message });
    }
  }
  for (const turn of turns) {
    pushTurn(turn);
  }
  return items;
}

/** True when the latest history match still waits for the user's choice. */
export function pendingHistoryMatch(detail: SessionDetail): HistoryMatchMessage | null {
  const matches = detail.messages.filter((message): message is HistoryMatchMessage => message.kind === "history_match");
  return matches.find((message) => message.resolution === null) ?? null;
}

// Sessions ------------------------------------------------------------------------

export type SessionStatus = "running" | "classified" | "clarifying" | "rejected" | "new";

export function sessionStatus(session: Pick<SessionSummary, "running" | "phase">): SessionStatus {
  if (session.running) return "running";
  return session.phase ?? "new";
}

/** The code a session row shows: the declared one, or else the chosen one. */
export function sessionCode(session: Pick<SessionSummary, "declaredCode" | "chosenCode">): string | null {
  return session.declaredCode ?? session.chosenCode ?? null;
}

export type SessionGroupKey = "today" | "yesterday" | "week" | "older";

/** Today, Yesterday, Previous 7 days, Older — by the last update, newest first. */
export function groupSessions<T extends Pick<SessionSummary, "updatedAt">>(sessions: T[], now = new Date()): Array<{ key: SessionGroupKey; sessions: T[] }> {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = 24 * 60 * 60 * 1000;
  const order: SessionGroupKey[] = ["today", "yesterday", "week", "older"];
  const buckets = new Map<SessionGroupKey, T[]>(order.map((key) => [key, []]));
  const sorted = [...sessions].sort((a, b) => (time(b.updatedAt) ?? 0) - (time(a.updatedAt) ?? 0));
  for (const session of sorted) {
    const updated = time(session.updatedAt) ?? 0;
    const key: SessionGroupKey =
      updated >= startOfToday ? "today" : updated >= startOfToday - day ? "yesterday" : updated >= startOfToday - 7 * day ? "week" : "older";
    buckets.get(key)?.push(session);
  }
  return order.map((key) => ({ key, sessions: buckets.get(key) ?? [] })).filter((group) => group.sessions.length > 0);
}

// Sources -------------------------------------------------------------------------

export const SOURCE_GROUPS: Source["kind"][] = ["product", "decision", "bti", "tariff", "search", "image", "upload", "other"];

/** Sources grouped by kind; inside a group, the newest turn first. */
export function groupSources(sources: Source[], turns: Turn[]): Array<{ kind: Source["kind"]; sources: Source[] }> {
  const turnOrder = new Map(turns.map((turn, index) => [turn.id, index]));
  const rank = (source: Source) => (source.turnId ? (turnOrder.get(source.turnId) ?? -1) : -1);
  const sorted = [...sources].sort((a, b) => {
    const byTurn = rank(b) - rank(a);
    if (byTurn !== 0) return byTurn;
    return (time(a.createdAt) ?? 0) - (time(b.createdAt) ?? 0);
  });
  return SOURCE_GROUPS.map((kind) => ({ kind, sources: sorted.filter((source) => source.kind === kind) })).filter(
    (group) => group.sources.length > 0,
  );
}
